/**
 * Typed HTTP client for the Avity API.
 *
 * Wraps `fetch` with three concerns the rest of the app shouldn't
 * care about:
 *
 *   1. **Auth header injection.** Every request reads the current
 *      access token from the auth store and attaches it as
 *      `Authorization: Bearer <token>`. No business code ever
 *      hand-builds the header.
 *   2. **Auto-refresh on 401 TOKEN_EXPIRED.** The single most
 *      annoying UX failure mode of V1 was "your session expired,
 *      log in again" mid-form. V2 silently swaps the token and
 *      replays the request once. If the refresh itself fails
 *      (refresh token expired or the user was deleted), the
 *      `onAuthFailure` callback fires — the app uses it to flush
 *      the store and route back to /login.
 *   3. **Envelope unwrapping.** The API wraps every success in
 *      `{ success: true, data, meta? }` and every failure in
 *      `{ success: false, error: { code, message, details? } }`.
 *      The client returns `data` directly on success and throws an
 *      `ApiError` on failure, so callers do `const u = await api.get(...)`
 *      not `const { data } = await api.get(...)`.
 *
 * The client is intentionally NOT a class. Three reasons:
 *   - Easier to swap in tests (factory + plain object).
 *   - No `this` foot-guns when the methods are passed around.
 *   - Mirrors the API repository pattern — pure functions + a
 *     factory that closes over its dependencies.
 */

const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? ''

/* -------------------------------------------------------------------------- */
/*  Public types                                                               */
/* -------------------------------------------------------------------------- */

export interface PaginationMeta {
  total: number
  page: number
  limit: number
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
}

export interface ApiClient {
  get<T>(path: string, query?: Record<string, string | number | boolean>): Promise<T>
  /**
   * Variant of `get` that preserves the full envelope (data + meta).
   * Use for paginated list endpoints where the caller needs the
   * pagination metadata as well as the rows.
   */
  getPaginated<T>(
    path: string,
    query?: Record<string, string | number | boolean>,
  ): Promise<PaginatedResponse<T>>
  post<T>(path: string, body?: unknown): Promise<T>
  patch<T>(path: string, body?: unknown): Promise<T>
  delete(path: string): Promise<void>
  /** Multipart upload for signatures, receipt photo, etc. */
  upload<T>(path: string, formData: FormData): Promise<T>
}

export class ApiError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ApiClientDeps {
  /** Returns the current access token (or null if logged out). */
  getAccessToken: () => string | null
  /** Performs a refresh and returns the new access token. */
  refreshAccessToken: () => Promise<string>
  /** Called when refresh fails — the caller flushes the auth store. */
  onAuthFailure: () => void
}

/* -------------------------------------------------------------------------- */
/*  Factory                                                                    */
/* -------------------------------------------------------------------------- */

export function createApiClient(deps: ApiClientDeps): ApiClient {
  const url = (path: string, query?: Record<string, string | number | boolean>) => {
    const base = `${API_BASE}${path}`
    if (query === undefined) return base
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') {
        params.append(k, String(v))
      }
    }
    const qs = params.toString()
    return qs === '' ? base : `${base}?${qs}`
  }

  /**
   * One-shot fetch that handles header injection + envelope
   * unwrapping. The retry-on-401 dance lives in `withAuthRetry`
   * just below.
   *
   * `mode` controls how the JSON envelope is returned:
   *   - `'data'`     → returns `body.data` (default; matches
   *                    the typical use case).
   *   - `'envelope'` → returns the full body so callers can read
   *                    `meta` for pagination.
   *   - `'none'`     → discards the body (used for 204-style
   *                    endpoints).
   */
  async function rawFetch(
    fullUrl: string,
    init: RequestInit,
    mode: 'data' | 'envelope' | 'none',
  ): Promise<unknown> {
    const token = deps.getAccessToken()
    const headers = new Headers(init.headers)
    if (token !== null) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const response = await fetch(fullUrl, { ...init, headers })

    // 204 No Content (e.g. DELETE / dismiss) — short-circuit.
    if (response.status === 204) {
      return undefined
    }

    // The API always returns JSON. If we ever get HTML back here it
    // means the proxy / build is misconfigured — surface it loudly.
    let body: unknown
    try {
      body = (await response.json()) as unknown
    } catch {
      throw new ApiError(
        'NETWORK_ERROR',
        'Réponse invalide du serveur',
        response.status,
      )
    }

    if (!response.ok) {
      const env = body as {
        error?: { code?: string; message?: string; details?: unknown }
      }
      throw new ApiError(
        env.error?.code ?? 'UNKNOWN_ERROR',
        env.error?.message ?? 'Erreur serveur',
        response.status,
        env.error?.details,
      )
    }

    if (mode === 'none') return undefined
    if (mode === 'envelope') return body
    const env = body as { data?: unknown }
    return env.data
  }

  /**
   * Wrap `rawFetch` with the auto-refresh dance. If the first
   * attempt fails with 401 TOKEN_EXPIRED, refresh once and retry.
   * Any other failure (or a refresh failure) propagates.
   */
  async function withAuthRetry<T>(
    fullUrl: string,
    init: RequestInit,
    mode: 'data' | 'envelope' | 'none',
  ): Promise<T> {
    try {
      return (await rawFetch(fullUrl, init, mode)) as T
    } catch (error) {
      const isExpired =
        error instanceof ApiError &&
        error.status === 401 &&
        error.code === 'TOKEN_EXPIRED'
      if (!isExpired) throw error

      try {
        await deps.refreshAccessToken()
      } catch (refreshError) {
        // Refresh failed → unrecoverable. Flush auth and bubble up
        // a 401 so the caller can route to /login.
        deps.onAuthFailure()
        throw refreshError instanceof ApiError
          ? refreshError
          : new ApiError(
              'AUTHENTICATION_REQUIRED',
              'Session expirée, veuillez vous reconnecter',
              401,
            )
      }

      // Replay with the freshly minted token.
      return (await rawFetch(fullUrl, init, mode)) as T
    }
  }

  const client: ApiClient = {
    async get<T>(
      path: string,
      query?: Record<string, string | number | boolean>,
    ): Promise<T> {
      return withAuthRetry<T>(url(path, query), { method: 'GET' }, 'data')
    },
    async getPaginated<T>(
      path: string,
      query?: Record<string, string | number | boolean>,
    ): Promise<PaginatedResponse<T>> {
      const env = await withAuthRetry<{
        success: true
        data: T[]
        meta: PaginationMeta
      }>(url(path, query), { method: 'GET' }, 'envelope')
      return { data: env.data, meta: env.meta }
    },
    async post<T>(path: string, body?: unknown): Promise<T> {
      const headers: HeadersInit =
        body === undefined ? {} : { 'Content-Type': 'application/json' }
      return withAuthRetry<T>(
        url(path),
        {
          method: 'POST',
          headers,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        },
        'data',
      )
    },
    async patch<T>(path: string, body?: unknown): Promise<T> {
      const headers: HeadersInit =
        body === undefined ? {} : { 'Content-Type': 'application/json' }
      return withAuthRetry<T>(
        url(path),
        {
          method: 'PATCH',
          headers,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        },
        'data',
      )
    },
    async delete(path: string): Promise<void> {
      await withAuthRetry<void>(url(path), { method: 'DELETE' }, 'none')
    },
    async upload<T>(path: string, formData: FormData): Promise<T> {
      // Don't set Content-Type — the browser fills it in with the
      // correct multipart boundary.
      return withAuthRetry<T>(url(path), { method: 'POST', body: formData }, 'data')
    },
  }
  return client
}
